<script setup lang="ts">
/**
 * BG §17.2 / BG §19.1 — the logo and headline treatment: a magenta and a cyan ghost sitting 1px either
 * side of dirty-white type, with an occasional short glitch.
 *
 * The ghosts are `aria-hidden` duplicates, so the text is announced once. The glitch fires on an
 * interval rather than a loop because BG §22.2 asks for *occasional* — a permanently glitching wordmark
 * is noise, and BG §22.3 rules out anything that flashes.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { MOTION } from '../tokens'

const props = withDefaults(
    defineProps<{
        text: string
        /** Heading level or element. The hero logo is a `div`; the headline is an `h1`. */
        as?: string
        /** Off for body copy, where chromatic edges only hurt legibility. */
        glitch?: boolean
    }>(),
    { as: 'span', glitch: true },
)

const glitching = ref(false)
let timer: ReturnType<typeof setInterval> | undefined

/** Someone who asked for less motion gets the chromatic edge but never the jump. */
function motionAllowed(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false
    }
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

onMounted(() => {
    if (!props.glitch || !motionAllowed()) {
        return
    }
    timer = setInterval(() => {
        glitching.value = true
        setTimeout(() => {
            glitching.value = false
        }, MOTION.glitchMs)
    }, MOTION.glitchIntervalMs)
})

onBeforeUnmount(() => {
    if (timer) {
        clearInterval(timer)
    }
})

const state = computed(() => (glitching.value ? 'on' : 'off'))
</script>

<template>
    <component :is="as" class="zh-glitch" :data-glitching="state">
        <span class="zh-glitch__ghost zh-glitch__ghost--magenta" aria-hidden="true">{{ text }}</span>
        <span class="zh-glitch__ghost zh-glitch__ghost--cyan" aria-hidden="true">{{ text }}</span>
        <span class="zh-glitch__face">{{ text }}</span>
    </component>
</template>

<style scoped>
.zh-glitch {
    position: relative;
    display: block;
    margin: 0;
    color: var(--dirty-white);
}

.zh-glitch__ghost,
.zh-glitch__face {
    display: block;
    grid-area: 1 / 1;
}

.zh-glitch__ghost {
    position: absolute;
    inset: 0;
    pointer-events: none;
    user-select: none;
}

.zh-glitch__ghost--magenta {
    color: var(--neon-magenta);
    transform: translateX(-0.055em);
    mix-blend-mode: screen;
    opacity: 0.55;
}

.zh-glitch__ghost--cyan {
    color: var(--electric-cyan);
    transform: translateX(0.055em);
    mix-blend-mode: screen;
    opacity: 0.55;
}

.zh-glitch__face {
    position: relative;
}

.zh-glitch[data-glitching='on'] .zh-glitch__ghost--magenta {
    transform: translate(-0.16em, -0.02em);
    opacity: 0.85;
}

.zh-glitch[data-glitching='on'] .zh-glitch__ghost--cyan {
    transform: translate(0.16em, 0.02em);
    opacity: 0.85;
}

.zh-glitch[data-glitching='on'] .zh-glitch__face {
    transform: translateX(0.02em);
}

@media (prefers-reduced-motion: reduce) {
    .zh-glitch[data-glitching='on'] .zh-glitch__ghost--magenta,
    .zh-glitch[data-glitching='on'] .zh-glitch__ghost--cyan,
    .zh-glitch[data-glitching='on'] .zh-glitch__face {
        transform: none;
    }
}
</style>
